import { createContext, useCallback, useEffect, useRef, useState } from "react";
import { signOut } from "firebase/auth";
import { auth as firebaseAuth } from "@/lib/firebase";
import {
  setUnauthorizedHandler,
  setPasswordChangeRequiredHandler,
} from "@/lib/api-client";
import { router } from "@/app/router";
import type { User, LoginRequest } from "@/types/api";
import { login as loginApi, getCurrentUser } from "../api/auth-api";

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (data: LoginRequest) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  clearRequiresPasswordChange: () => void;
  refresh: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // UID of the last user we successfully loaded `/auth/me` for. Lets us skip
  // the re-fetch on token-refresh callbacks (Firebase fires `onAuthStateChanged`
  // every hour even when the same user is still signed in).
  const loadedUidRef = useRef<string | null>(null);
  // Only the very first auth resolution should show a spinner; later token
  // refreshes happen silently.
  const initialLoadDoneRef = useRef(false);

  const loadUser = useCallback(async () => {
    try {
      const response = await getCurrentUser();
      if (response.success && response.data) {
        setUser(response.data);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = firebaseAuth.onAuthStateChanged(async (fbUser) => {
      const nextUid = fbUser?.uid ?? null;
      const prevUid = loadedUidRef.current;

      if (nextUid === null) {
        loadedUidRef.current = null;
        setUser(null);
        if (!initialLoadDoneRef.current) {
          initialLoadDoneRef.current = true;
          setIsLoading(false);
        }
        return;
      }

      // Same UID as last load — Firebase silently rotated the token. Don't
      // re-fetch `/auth/me`; the request interceptor will use the fresh token
      // on the next outgoing call.
      if (nextUid === prevUid) {
        return;
      }

      loadedUidRef.current = nextUid;
      await loadUser();
      if (!initialLoadDoneRef.current) {
        initialLoadDoneRef.current = true;
        setIsLoading(false);
      }
    });
    return unsubscribe;
  }, [loadUser]);

  // Wire the api-client's 401 / 403 handlers so they navigate via the router
  // (preserving React state + unsaved input) rather than hard-reloading.
  // We use `router.navigate` rather than `useNavigate` because the
  // `AuthProvider` lives outside the `RouterProvider` in the tree.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      loadedUidRef.current = null;
      if (window.location.pathname !== "/login") {
        void router.navigate("/login", { replace: true });
      }
    });
    setPasswordChangeRequiredHandler(() => {
      if (window.location.pathname !== "/change-password") {
        void router.navigate("/change-password", { replace: true });
      }
    });
  }, []);

  const login = async (data: LoginRequest) => {
    try {
      await loginApi(data);
      // `onAuthStateChanged` will fire with the new Firebase user and trigger
      // `loadUser()`. We do NOT setUser directly here — that would race
      // against the `/auth/me` fetch.
      return { success: true };
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "An error occurred during login";
      return { success: false, error: message };
    }
  };

  const logout = () => {
    // Fire-and-forget; never block the UI on Firebase sign-out completing.
    void signOut(firebaseAuth).catch(() => {
      /* swallow — we've already cleared local state */
    });
    loadedUidRef.current = null;
    setUser(null);
  };

  const clearRequiresPasswordChange = () => {
    setUser((prev) => {
      if (!prev) return prev;
      return { ...prev, requiresPasswordChange: false };
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        clearRequiresPasswordChange,
        refresh: loadUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
